/**
 * Wallet-connect telemetry — a small, dashboard-ready event sink.
 *
 * Purpose: give us a *stable category list* for wallet connect/disconnect outcomes so the connect
 * funnel (and specifically the Phantom-Solana connect-hang, issue #80) is observable at a glance on a
 * dashboard later. Every event carries a `category` from `WALLET_EVENT` — that fixed enum IS the
 * "category list" to group by in the dash.
 *
 * Sink model (no backend endpoint exists yet, by design):
 *   1. Always `console` with a stable `[wallet-telemetry]` prefix + structured JSON (greppable now).
 *   2. If `VITE_WALLET_TELEMETRY_URL` is set, best-effort `navigator.sendBeacon` to it (fire-and-forget,
 *      never throws, never blocks the UI). Unset (default) → console only, so we don't 404-spam.
 *
 * This module has NO React deps and never throws — telemetry must not be able to break a connect flow.
 */

/** The fixed category list — group by this on the dashboard. Add here, never repurpose an existing value. */
export const WALLET_EVENT = {
  /** User initiated a connect (row tapped / connect() about to be driven). */
  ATTEMPT: 'connect_attempt',
  /** Adapter reported connected (publicKey available). Terminal success. */
  SUCCESS: 'connect_success',
  /** User declined the wallet prompt (4001 / "rejected" / "cancelled"). Terminal, benign. */
  USER_REJECTED: 'connect_user_rejected',
  /** WalletAccountError: wallet connected but active account has no address for this chain
   *  (e.g. Phantom on a non-Solana account → null publicKey). Terminal, actionable. */
  ACCOUNT_MISMATCH: 'connect_account_mismatch',
  /** WalletNotReadyError: adapter not Installed/Loadable when connect was attempted. */
  NOT_READY: 'connect_not_ready',
  /** connect() resolved as a no-op because a connect was already in-flight (adapter #connecting guard,
   *  adapter.js:214 / WalletProviderBase:185). The de-race diagnostic — should trend to ~0 after #80. */
  NOOP_EARLY_RETURN: 'connect_noop_early_return',
  /** connect resolved/settled but `connected` never flipped and no error surfaced — the stranded spinner
   *  that previously forced a hard refresh (#80). We surface a non-destructive affordance instead. */
  STRANDED: 'connect_stranded_spinner',
  /** adapter.connecting has been stuck `true` past the hang threshold — the rare genuinely-hung provider
   *  promise (adapter.js:221 never settled). Only a page reload resets this. */
  HANG_SUSPECTED: 'connect_hang_suspected',
  /** The "taking longer than expected — Cancel / Retry" affordance was shown to the user. */
  AFFORDANCE_SHOWN: 'connect_affordance_shown',
  /** User asked to retry from the affordance (or re-tapped the wallet). */
  RETRY: 'connect_retry',
  /** Last-resort recovery: full page reload to reset a poisoned adapter instance (#80). */
  RELOAD_RECOVERY: 'connect_reload_recovery',
  /** A generic/unclassified connect error. Inspect `errorName`/`message` to grow the taxonomy. */
  UNKNOWN_ERROR: 'connect_unknown_error',
} as const

export type WalletEventCategory = (typeof WALLET_EVENT)[keyof typeof WALLET_EVENT]

export type WalletChain = 'solana' | 'evm' | 'tron'

export interface WalletEventDetail {
  chain: WalletChain
  /** Wallet display name (e.g. "Phantom", "Solflare", "MetaMask"). */
  wallet?: string | null
  /** Error class name when applicable (e.g. "WalletAccountError"). */
  errorName?: string | null
  /** Human-readable message (already sanitized — never include secrets/keys). */
  message?: string | null
  /** Free-form small scalars for the dashboard (e.g. elapsedMs, attempt). No PII, no addresses. */
  [k: string]: string | number | boolean | null | undefined
}

/**
 * Map a thrown wallet error to a stable category. Mirrors the adapter's error classes
 * (`@solana/wallet-adapter-base`) and the codes wallets emit on user cancel.
 */
export function categorizeWalletError(e: unknown): WalletEventCategory {
  const err = e as { name?: string; code?: number; message?: string } | null
  const name = err?.name ?? ''
  const msg = String(err?.message ?? '')
  if (err?.code === 4001 || /reject|cancel|denied/i.test(msg)) return WALLET_EVENT.USER_REJECTED
  if (name === 'WalletAccountError') return WALLET_EVENT.ACCOUNT_MISMATCH
  if (name === 'WalletNotReadyError' || name === 'WalletNotSelectedError') return WALLET_EVENT.NOT_READY
  return WALLET_EVENT.UNKNOWN_ERROR
}

const TELEMETRY_URL = (import.meta.env.VITE_WALLET_TELEMETRY_URL as string | undefined) || ''

/**
 * Emit a wallet telemetry event. Never throws. Console always; beacon only if a URL is configured.
 */
export function emitWalletEvent(category: WalletEventCategory, detail: WalletEventDetail): void {
  const evt = {
    kind: 'wallet_event',
    category,
    ...detail,
    // Relative time is fine for grouping; absolute timestamps are stamped server-side on ingest.
    t: typeof performance !== 'undefined' ? Math.round(performance.now()) : undefined,
  }
  try {
    // Structured, greppable, and dashboard-ready. USER_REJECTED is benign → info, not error.
    const isBenign = category === WALLET_EVENT.USER_REJECTED || category === WALLET_EVENT.SUCCESS ||
      category === WALLET_EVENT.ATTEMPT || category === WALLET_EVENT.RETRY
    const line = `[wallet-telemetry] ${category} ${JSON.stringify(evt)}`
    if (isBenign) console.info(line)
    else console.warn(line)
  } catch {
    /* console must never break a connect flow */
  }
  try {
    if (TELEMETRY_URL && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(TELEMETRY_URL, new Blob([JSON.stringify(evt)], { type: 'application/json' }))
    }
  } catch {
    /* beacon is best-effort; a failed send must never surface to the user */
  }
}
